import { useCallback } from "react";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { useU2000GetHandlers, type U2000GetHandlersParams, type U2000GetHandlersReturn } from "../u2000Core/hooks/handler/U2000UseGetHandlers";
import type { U2000InitLUHandlersParams, U2000InitLUHandlersReturn } from "../u2000Core/hooks/handler/U2000UseInitLUHandlers";
import { useU2000InitLUHandlers } from "../u2000Core/hooks/handler/U2000UseInitLUHandlers";
import { useU2000SaveHandlers, type U2000SaveHandlersParams, type U2000SaveHandlersReturn } from "../u2000Core/hooks/handler/U2000UseSaveHandlers";
import { useU2000SearchHandlers, type U2000SearchHandlersParams, type U2000SearchHandlersReturn } from "../u2000Core/hooks/handler/U2000UseSearchHandlers";
import { U2000FormStateEnumEditMode } from "../u2000Core/reducer/formReducer/U2000FormState";
import {
  buildCreateBody,
  buildUpdateBody,
  doCreateData,
  doGenerateWip,
  doGetData,
  doGetGenerateWipJob,
  doInitLU,
  doSearch,
  doUpdateData,
  parseGenerateWipPostResponse,
  parseJobStatusGetResponse,
  type U3134InitLU,
  type U3134WorkInProgressFormRow,
  type U3134GenerateWipJob,
  type U3134WorkInProgressSearchRow,
} from "./U3134Service";

/** Recherche U3134 : axios encapsulé (ne pas appeler `doSearch` depuis le composant). */
export function useU3134SearchHandlers<T = U3134WorkInProgressSearchRow>(
  params: Omit<U2000SearchHandlersParams<T>, "doSearchFn">,
): U2000SearchHandlersReturn {
  const axios = useU2000AxiosAuthenticated();
  const doSearchFn = useCallback((searchPayload: Parameters<typeof doSearch>[1]) => doSearch(axios, searchPayload), [axios]);
  return useU2000SearchHandlers<T>({ ...params, doSearchFn });
}

/** Lecture d’une ligne U3134. */
export function useU3134GetHandlers<T extends { id: string } = U3134WorkInProgressFormRow>(
  params: Omit<U2000GetHandlersParams<T>, "doGetFn">,
): U2000GetHandlersReturn {
  const axios = useU2000AxiosAuthenticated();
  const doGetFn = useCallback((id: string) => doGetData(axios, id), [axios]);
  return useU2000GetHandlers<T>({ ...params, doGetFn });
}

/** Création / mise à jour U3134 (corps construits dans le hook). */
export function useU3134SaveHandlers(
  params: Omit<U2000SaveHandlersParams<U3134WorkInProgressFormRow>, "doSaveFn">,
): U2000SaveHandlersReturn<U3134WorkInProgressFormRow> {
  const axios = useU2000AxiosAuthenticated();
  const doSaveFn = useCallback(
    (row: U3134WorkInProgressFormRow, editMode: (typeof U2000FormStateEnumEditMode)[keyof typeof U2000FormStateEnumEditMode]) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateData(axios, buildCreateBody(row));
      }
      if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(axios, row.id, buildUpdateBody(row));
      }
      return Promise.resolve(undefined);
    },
    [axios],
  );
  return useU2000SaveHandlers<U3134WorkInProgressFormRow>({ ...params, doSaveFn });
}

/** initLU U3134. */
export function useU3134InitLUHandlers(params: Omit<U2000InitLUHandlersParams<U3134InitLU>, "doInitLUFn">): U2000InitLUHandlersReturn {
  const axios = useU2000AxiosAuthenticated();
  const doInitLUFn = useCallback(() => doInitLU(axios), [axios]);
  return useU2000InitLUHandlers<U3134InitLU>({ ...params, doInitLUFn });
}

/** Lancement du calcul TEC (POST /U3134/generateWip). */
export function useU3134GenerateWipHandlers() {
  const axios = useU2000AxiosAuthenticated();
  return useCallback(async (): Promise<U3134GenerateWipJob> => {
    const response = await doGenerateWip(axios);
    return parseGenerateWipPostResponse(response.data);
  }, [axios]);
}

/** Polling du statut du job TEC (GET /U3134/jobs/:jobId, droits U3134:GENERATE). */
export function useU3134GetGenerateWipJobHandlers() {
  const axios = useU2000AxiosAuthenticated();
  return useCallback(async (jobId: string): Promise<U3134GenerateWipJob> => {
    const response = await doGetGenerateWipJob(axios, jobId);
    return parseJobStatusGetResponse(response.data);
  }, [axios]);
}
